import { AnalyticsStore, ContextStore, ExperimentStore } from 'bernie-plugin-mobx';
import { autorun, comparer } from 'mobx';
import { inject, observer } from 'mobx-react';
import { isEqual } from 'lodash';
import { useHistory, useLocation } from 'react-router-dom';
import * as React from 'react';

import { merge, updateSearch } from 'bernie-core';

import { DynamicSearchMap } from '@shared-ui/lodging-property-search';
import { useLocalization } from '@shared-ui/localization-context';

import { getPropertySearchInput } from 'stores/adapters';
import { MultiItemStore } from 'src/common/stores/multi-item-store';
import { SearchCriteriaModel } from 'src/common/stores/models/search-criteria-model';
import { SearchResultsStore } from 'src/common/stores/search-results-store';

import { checkPropertyClusterLabelKey } from './eg-maps-utils';

import {
  ContextInput,
  DestinationInput,
  PaginationInput,
  PropertyDateRangeInput,
  PropertyMarketingInfoInput,
  PropertySearchCriteriaInput,
  PropertySearchFiltersInput,
  PropertyShopOptionsInput,
  PropertySort,
  RoomInput,
  SearchIntentInput,
  ShoppingContextInput,
} from 'src/common/__generated__/api';
import { EGMapConfiguration, FormatFunction } from '@eg-maps/core';

interface DynamicSearchMapWrapperProps {
  context?: ContextStore;
  searchCriteria?: SearchCriteriaModel;
  multiItem?: MultiItemStore;
  searchResults?: SearchResultsStore;
  experiment?: ExperimentStore;
  analytics?: AnalyticsStore;
  cfg?: EGMapConfiguration;
  clientPadding?: string;
  onMapDialogClosed?: () => void;
  filterComponent?: React.ReactNode;
  triggerRef?: React.Ref<HTMLElement>;
}

interface PropertySearchDynamicSearchMapQueryVariables {
  context: ContextInput;
  destination: DestinationInput;
  rooms?: RoomInput[] | null;
  dateRange?: PropertyDateRangeInput | null;
  sort?: PropertySort | null;
  filters?: PropertySearchFiltersInput | null;
  marketing?: PropertyMarketingInfoInput | null;
  propertyShopOptions?: PropertyShopOptionsInput | null;
  searchPagination?: PaginationInput | null;
  searchIntent?: SearchIntentInput | null;
  shoppingContext?: ShoppingContextInput | null;
  criteria?: PropertySearchCriteriaInput | null;
}
const { allKeys, flattenFields, withoutKeys } = merge;

export const DynamicSearchMapWrapper: React.FC<DynamicSearchMapWrapperProps> = inject(
  'context',
  'searchCriteria',
  'multiItem',
  'searchResults',
  'experiment',
  'analytics'
)(
  observer(
    ({
      context,
      searchCriteria,
      multiItem,
      searchResults,
      experiment,
      analytics,
      cfg,
      clientPadding,
      onMapDialogClosed,
      filterComponent,
      triggerRef,
    }) => {
      const history = useHistory();
      const location = useLocation();
      const l10n = useLocalization();

      if (!context || !searchResults?.searchCriteria) return null;

      const calculateMapInputs = React.useCallback(() => {
        const paginationPreviousSubset = searchResults?.pagination?.subSets?.previousSubset;
        const paginationInput: PaginationInput = {
          size: paginationPreviousSubset?.size,
          startingIndex: (paginationPreviousSubset?.startingIndex ?? 0) + (paginationPreviousSubset?.size ?? 0),
        };

        const {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          context: bexApiContext,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          returnPropertyType,
          ...rest
        } = getPropertySearchInput(
          context,
          searchResults?.searchCriteria,
          multiItem?.multiItemContext,
          false,
          paginationInput,
          experiment,
          searchResults?.pageData?.pageId
        );

        return { ...rest } as PropertySearchDynamicSearchMapQueryVariables;
      }, [context, multiItem, searchResults, experiment]);

      const [mapInputs, setMapInputs] = React.useState<PropertySearchDynamicSearchMapQueryVariables>(calculateMapInputs());

      React.useEffect(() => {
        autorun(() => {
          const newState = calculateMapInputs();
          setMapInputs(prevState => {
            const [prevStateWithoutDestination, newStateWithoutDestination] = [
              withoutKeys(untrackedDestinationKeys).merge({}, flattenFields(prevState)),
              withoutKeys(untrackedDestinationKeys).merge({}, flattenFields(newState)),
            ];
            const forceRerender = !isEqual(prevStateWithoutDestination, newStateWithoutDestination);
            if (forceRerender) return newState;

            const [prevDestination, newDestination] = [prevState?.criteria?.primary.destination, newState.criteria?.primary.destination];
            const coordsAreEqual = comparer.structural(
              allKeys().merge({}, prevDestination?.coordinates),
              allKeys().merge({}, newDestination?.coordinates)
            );
            const regionNamesAreEqual = prevDestination?.regionName === newDestination?.regionName;
            const newRegionId = newState.criteria?.primary?.destination?.regionId;

            /*
              1)We only want to initialize a render whenever the regionId is calulated by property listing
              to prevent the map from re-rendering twice on map pan.

              2)Due to the way dialog context and the universal search form compete to update the URL we should only render
              the map whenever we see that the regionNames are different; preventing the double render of the map caused by the universal search form's destination input changing.
            */

            if ((coordsAreEqual && newRegionId !== null) || (coordsAreEqual && !regionNamesAreEqual)) {
              return prevState;
            }

            return newState;
          });
        });
      }, [context, multiItem, searchResults, experiment]);

      const trackMapSearch: () => void = analytics?.clickTracker
        ? analytics?.clickTracker('Module.Map.Search.Button', 'User clicking the map search button on PWA HSR Map', null, () => null)
        : () => null;

      const onDestinationChanged = (destination: DestinationInput) => {
        const latLong = destination.coordinates
          ? `${destination.coordinates.latitude},${destination.coordinates.longitude}`
          : 'undefined,undefined';
        const mapBounds = destination.mapBounds
          ? [
              [destination.mapBounds[0].latitude, destination.mapBounds[0].longitude],
              [destination.mapBounds[1].latitude, destination.mapBounds[1].longitude],
            ]
          : null;
        trackMapSearch();

        updateSearch({
          history,
          location,
          newParams: {
            regionId: '',
            destination: '',
            selected: '',
            poi: '',
            latLong,
            // developer note, since this bernie function only accepts strings we would need to keep the ts-ignore here
            // to resolve type issues when querying with the mapBounds
            // @ts-ignore
            mapBounds,
            neighborhood: '',
          },
        });
      };

      const translate: FormatFunction = React.useCallback((key, args) => checkPropertyClusterLabelKey(key, l10n, ...args), [l10n]);

      const egMapsConfig: EGMapConfiguration = React.useMemo(
        () =>
          ({
            ...cfg,
            i18n: {
              formatFunction: translate,
            },
          } as EGMapConfiguration),
        [cfg, translate]
      );

      const fullscreenOptions = onMapDialogClosed
        ? {
            onClose: onMapDialogClosed,
            isDialogOpen: true,
            triggerRef: triggerRef ?? null,
          }
        : undefined;

      return (
        <DynamicSearchMap
          inputs={mapInputs}
          selectedFeatureId={searchCriteria?.getHighlightedPropertyId}
          clientPadding={clientPadding}
          fullscreenOptions={fullscreenOptions}
          egMapsConfig={egMapsConfig}
          filterComponent={filterComponent}
          onDestinationChanged={onDestinationChanged}
          height={'100%'}
        />
      );
    }
  )
);

export const untrackedDestinationKeys = [
  'criteria_primary_destination_regionName',
  'criteria_primary_destination_regionId',
  'criteria_primary_destination_coordinates',
  'criteria_primary_destination_pinnedPropertyId',
  'criteria_primary_destination_propertyIds',
  'criteria_primary_destination_mapBounds',
  'criteria_primary_destination_coordinates_latitude',
  'criteria_primary_destination_coordinates_longitude',
];

DynamicSearchMapWrapper.displayName = 'DynamicSearchMapWrapper';
